import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  useToast,
  type Column,
} from "../../components/ui";
import {
  adminService,
  type AdminOrganization,
  type OrganizationInput,
} from "../../services/admin.service";
import { formatNumber } from "../../lib/format";

const tierLabels: Record<string, string> = {
  master: "Master",
  franqueado: "Franqueado",
  cliente: "Cliente",
};

const emptyForm: OrganizationInput = {
  name: "",
  tier: "cliente",
  parent_id: "",
  document: "",
  status: "active",
};

/**
 * Hierarquia de organizações. O backend limita quais níveis o ator cria
 * e recusa mover uma organização para fora (ou dentro) da própria árvore.
 */
export default function OrganizationsPage() {
  const toast = useToast();
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [tiers, setTiers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminOrganization | null>(null);
  const [form, setForm] = useState<OrganizationInput>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<AdminOrganization | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.organizations();
      setOrganizations(result.data);
      setTiers(result.meta?.assignable_tiers ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyForm, tier: tiers[tiers.length - 1] ?? "cliente" });
    setDialogOpen(true);
  }

  function openEdit(organization: AdminOrganization) {
    setEditing(organization);
    setForm({
      name: organization.name,
      tier: organization.tier,
      parent_id: organization.parent_id ?? "",
      document: organization.document ?? "",
      status: organization.status,
    });
    setDialogOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      const payload: OrganizationInput = {
        ...form,
        parent_id: form.parent_id ? form.parent_id : null,
        document: form.document ? form.document : null,
      };
      if (editing) {
        await adminService.updateOrganization(editing.id, payload);
        toast.success("Organização atualizada.");
      } else {
        await adminService.createOrganization(payload);
        toast.success("Organização criada.");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!removing) return;
    try {
      await adminService.deleteOrganization(removing.id);
      toast.success("Organização removida.");
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setRemoving(null);
    }
  }

  const columns: Column<AdminOrganization>[] = [
    { key: "name", header: "Nome", render: (o) => o.name },
    { key: "tier", header: "Nível", render: (o) => <Badge>{tierLabels[o.tier] ?? o.tier}</Badge> },
    { key: "parent", header: "Acima", render: (o) => o.parent_name ?? "—" },
    {
      key: "status",
      header: "Status",
      render: (o) => (
        <Badge tone={o.status === "active" ? "ok" : "warn"}>
          {o.status === "active" ? "Ativa" : o.status}
        </Badge>
      ),
    },
    { key: "projects", header: "Projetos", render: (o) => formatNumber(o.projects_count ?? 0) },
    { key: "members", header: "Usuários", render: (o) => formatNumber(o.members_count ?? 0) },
    {
      key: "actions",
      header: "",
      render: (o) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => openEdit(o)}>
            Editar
          </Button>
          <Button variant="ghost" onClick={() => setRemoving(o)}>
            Remover
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Organizações"
        description="Hierarquia da sua estrutura: masters, franqueados e clientes."
        actions={tiers.length > 0 ? <Button onClick={openCreate}>Nova organização</Button> : undefined}
      />

      <Card>
        {loading ? (
          <SkeletonRows rows={5} />
        ) : error ? (
          <EmptyState title="Não foi possível carregar" description={error} />
        ) : organizations.length === 0 ? (
          <EmptyState title="Nenhuma organização" description="Crie a primeira da sua estrutura." />
        ) : (
          <Table rows={organizations} columns={columns} rowKey={(o) => o.id} />
        )}
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar organização" : "Nova organização"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void save()} loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Select
            label="Nível"
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            options={tiers.map((tier) => ({ value: tier, label: tierLabels[tier] ?? tier }))}
          />
          <Select
            label="Organização acima"
            value={form.parent_id ?? ""}
            onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
            options={[
              { value: "", label: "Nenhuma (topo do seu escopo)" },
              ...organizations
                .filter((o) => o.id !== editing?.id)
                .map((o) => ({ value: o.id, label: `${o.name} (${tierLabels[o.tier] ?? o.tier})` })),
            ]}
          />
          <Input
            label="Documento"
            value={form.document ?? ""}
            onChange={(e) => setForm({ ...form, document: e.target.value })}
          />
          <Select
            label="Status"
            value={form.status ?? "active"}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            options={[
              { value: "active", label: "Ativa" },
              { value: "suspended", label: "Suspensa" },
              { value: "cancelled", label: "Cancelada" },
            ]}
          />
        </div>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title="Remover organização"
        description={`Remover ${removing?.name}? Só é possível se não houver filhas nem projetos.`}
        confirmLabel="Remover"
        onCancel={() => setRemoving(null)}
        onConfirm={() => void remove()}
      />
    </>
  );
}
